// Non-extractable AES-GCM wrap key persisted in IndexedDB.
// Once created it CANNOT be exfiltrated by JavaScript (Web Crypto guarantees
// non-extractable keys are opaque handles). It can still be *used* by any
// script running on this origin, so this defends against out-of-band
// localStorage exfiltration (extensions, backups, log leaks) — NOT against
// arbitrary XSS inside the app.

const DB_NAME = "ol-keystore";
const STORE = "keys";
const KEY_ID = "wrapKey";
let pendingWrapKey = null;

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbGet(key) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).get(key);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbPut(key, value) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function idbAdd(key, value) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    const req = tx.objectStore(STORE).add(value, key);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

async function idbDel(key) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function createWrapKeySafely() {
  const existing = await idbGet(KEY_ID);
  if (existing) return existing;

  const generated = await crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    false, // extractable: false — key cannot be leaked, only used
    ["encrypt", "decrypt"],
  );

  try {
    // `add`, unlike `put`, never overwrites a key created by another tab.
    // If two registration flows race, only the first CryptoKey wins and all
    // callers subsequently read that same durable key.
    await idbAdd(KEY_ID, generated);
    return generated;
  } catch (error) {
    if (error?.name !== "ConstraintError") throw error;
    const winner = await idbGet(KEY_ID);
    if (!winner) throw error;
    return winner;
  }
}

/** Returns the existing wrap key without ever replacing a missing key. */
export async function getExistingWrapKey() {
  if (pendingWrapKey) return pendingWrapKey;
  return idbGet(KEY_ID);
}

/** Ask the browser not to evict the identity keystore during storage pressure. */
export async function requestPersistentKeyStorage() {
  try {
    if (typeof navigator === "undefined" || !navigator.storage?.persist) return false;
    if (await navigator.storage.persisted?.()) return true;
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}

/** Returns the app's wrap key, creating a fresh one on first registration. */
export async function getOrCreateWrapKey() {
  if (!pendingWrapKey) {
    pendingWrapKey = createWrapKeySafely().finally(() => {
      pendingWrapKey = null;
    });
  }
  return pendingWrapKey;
}

/** Removes the wrap key (called on logout or verified local recovery). */
export async function destroyWrapKey() {
  pendingWrapKey = null;
  await idbDel(KEY_ID);
}

function bufToB64(buf) {
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin);
}

function b64ToBuf(s) {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}

/** AES-GCM encrypt an UTF-8 string. Returns {iv, ct} base64. */
export async function encryptString(key, plaintext) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const data = new TextEncoder().encode(plaintext);
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, data);
  return { iv: bufToB64(iv.buffer), ct: bufToB64(ct) };
}

/** AES-GCM decrypt to an UTF-8 string. */
export async function decryptString(key, envelope) {
  const iv = b64ToBuf(envelope.iv);
  const ct = b64ToBuf(envelope.ct);
  const pt = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: new Uint8Array(iv) },
    key,
    ct,
  );
  return new TextDecoder().decode(pt);
}

// Retained for compatibility with any older imports/tests that may persist a
// replacement key explicitly. Normal code should use getOrCreateWrapKey().
export async function replaceWrapKeyForRecovery(key) {
  pendingWrapKey = null;
  await idbPut(KEY_ID, key);
}
